import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SocialFeeds from "../components/SocialFeeds";
import VideoGallery from "../components/VideoGallery";
import VideoSlider from "../components/WelcomeVideo";
import HotelCards from "../components/HotelCards";
import AboutUs from "../components/AboutSection";
import DalmaWildlifePromo from "../components/DalmaWildlifePromo";
import PopularDestination from "../components/PopularDestination";
import Newaboutus from "../components/Newaboutus";
import FaqSection from "../components/FaqSection";
import { REACT_APP_API_URL } from "../env";
import axios from "axios";

const API_BASE = REACT_APP_API_URL.replace(/\/$/, "");

const Homepage = ({ setDestinations }) => {
  const [showEventModal, setShowEventModal] = useState(false);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [desktopVideo1, setDesktopVideo1] = useState("");
  const [mobileVideo1, setMobileVideo1] = useState("");
  const [desktopVideo2, setDesktopVideo2] = useState("");
  const [mobileVideo2, setMobileVideo2] = useState("");
  const [homeBanner, setHomeBanner] = useState({
    imagePath: "",
    bannerType: "tour",
    redirectPath: "/tours",
    buttonLink: "/tours",
    buttonActionType: "internal",
    buttonText: "Enquire Now",
    buttonColor: "",
    buttonSizeDesktop: 12,
    buttonSizeMobile: 11,
    buttonBottomDesktop: 118,
    buttonBottomMobile: 118,
  });

  const resolveBannerAsset = (assetPath) => {
    if (!assetPath) return "";

    if (/^(https?:|data:|blob:)/i.test(assetPath)) {
      return assetPath;
    }

    if (
      assetPath.startsWith("/homeBannerUploads/") ||
      assetPath.startsWith("/videoUploads/")
    ) {
      return `${API_BASE}${assetPath}`;
    }

    return "";
  };

  const isValidExternalUrl = (url) => /^https?:\/\//i.test(url);

  const handleBannerRedirect = () => {
    const buttonLink = String(
      homeBanner.buttonLink || homeBanner.redirectPath || ""
    ).trim();
    if (!buttonLink) return;

    const buttonActionType = String(
      homeBanner.buttonActionType ||
      (isValidExternalUrl(buttonLink) ? "external" : "internal")
    ).toLowerCase();

    if (buttonActionType === "external") {
      if (!isValidExternalUrl(buttonLink)) return;
      window.open(buttonLink, "_blank");
      return;
    }

    navigate(buttonLink);
  };

  const resolveBottomValue = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getBannerButtonStyle = () => {
    const dynamicFontSize = isMobile
      ? resolveBottomValue(homeBanner.buttonSizeMobile, 11)
      : resolveBottomValue(homeBanner.buttonSizeDesktop, 12);
    const dynamicBottom = isMobile
      ? resolveBottomValue(homeBanner.buttonBottomMobile, 118)
      : resolveBottomValue(homeBanner.buttonBottomDesktop, 118);
    const verticalPadding = Math.max(6, Math.round(dynamicFontSize * 0.6));
    const horizontalPadding = Math.max(10, Math.round(dynamicFontSize));

    return {
      left: "50%",
      right: "auto",
      top: "auto",
      transform: "translateX(-50%)",
      bottom: `${dynamicBottom}px`,
      fontSize: `${dynamicFontSize}px`,
      padding: `${verticalPadding}px ${horizontalPadding}px`,
      ...(homeBanner.buttonColor ? { background: homeBanner.buttonColor } : {}),
    };
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowEventModal(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 768;
      setIsMobile((prev) => (prev === nextIsMobile ? prev : nextIsMobile));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setShowEventModal(true);
    const fetchHomepageVideos = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard/admin/list-all-videos?mode=active`);
        const rows = res.data.data || [];
        const row = Array.isArray(rows) ? rows[0] : rows;
        if (!row) return;
        if (row.vchVideo1Mode === "file") {
          setDesktopVideo1(row.vchVideo1DesktopPath ? `${API_BASE}${row.vchVideo1DesktopPath}` : "");
          setMobileVideo1(row.vchVideo1MobilePath ? `${API_BASE}${row.vchVideo1MobilePath}` : "");
        } else {
          setDesktopVideo1(row.vchVideo1DesktopLink);
          setMobileVideo1(row.vchVideo1MobileLink || row.vchVideo1DesktopLink);
        }
        if (row.vchVideo2Mode === "file") {
          setDesktopVideo2(row.vchVideo2DesktopPath ? `${API_BASE}${row.vchVideo2DesktopPath}` : "");
          setMobileVideo2(row.vchVideo2MobilePath ? `${API_BASE}${row.vchVideo2MobilePath}` : "");
        } else {
          setDesktopVideo2(row.vchVideo2DesktopLink);
          setMobileVideo2(row.vchVideo2MobileLink || row.vchVideo2DesktopLink);
        }
      } catch (err) {
        console.error("Failed to load homepage videos", err);
      }
    };

    const fetchHomeBanner = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/dashboard/admin/home-banners/active`);
        const banner = res.data?.data;
        if (!banner) return;

        setHomeBanner({
          imagePath: banner.imagePath || "",
          bannerType: banner.bannerType || "tour",
          redirectPath: banner.redirectPath || (banner.bannerType === "event" ? "/events" : "/tours"),
          buttonLink:
            banner.buttonLink ||
            banner.redirectPath ||
            (banner.bannerType === "event" ? "/events" : "/tours"),
          buttonActionType:
            banner.buttonActionType ||
            (isValidExternalUrl(banner.buttonLink || banner.redirectPath || "")
              ? "external"
              : "internal"),
          buttonText:
            banner.buttonText ||
            (banner.bannerType === "event" ? "Register Now" : "Enquire Now"),
          buttonColor: banner.buttonColor || "",
          buttonSizeDesktop: banner.buttonSizeDesktop ?? 12,
          buttonSizeMobile: banner.buttonSizeMobile ?? 11,
          buttonBottomDesktop: banner.buttonBottomDesktop ?? 118,
          buttonBottomMobile: banner.buttonBottomMobile ?? 118,
        });
      } catch (err) {
        console.error("Failed to load homepage banner", err);
      }
    };

    fetchHomepageVideos();
    fetchHomeBanner();
  }, []);

  return (
    <div className="homepage-container">
      <VideoSlider desktopVideoSrc={desktopVideo1} mobileVideoSrc={mobileVideo1} />
      <VideoSlider desktopVideoSrc={desktopVideo2} mobileVideoSrc={mobileVideo2} />

      <AboutUs />
      <HotelCards />
      <DalmaWildlifePromo />
      <PopularDestination setDestinations={setDestinations} />
      <VideoGallery />
      <SocialFeeds />
      <Newaboutus />
      <FaqSection />

      {showEventModal && !!homeBanner.imagePath && (
        <div className="event-overlay">
          <div className="event-modal-box">

            {/* Close Button */}
            <button
              className="event-close-btn"
              onClick={() => setShowEventModal(false)}
            >
              ✕
            </button>

            {/* Image Container */}
            <div className="event-image-wrapper">
              <img
                src={resolveBannerAsset(homeBanner.imagePath)}
                alt={homeBanner.bannerType === "event" ? "Events" : "Tours"}
              />

              {/* Overlay Button */}
              <button
                className="event-overlay-btn"
                onClick={handleBannerRedirect}
                style={getBannerButtonStyle()}
              >
                {homeBanner.buttonText ||
                  (homeBanner.bannerType === "event" ? "Register Now" : "Enquire Now")}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Homepage;
